import { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { transition } from 'styles/transitions';
import DisputeResolveModal from 'components/support/dispute-resolve-modal/DisputeResolveModal';
import { StatusBadge } from 'components/styled/Badges';
import NoDataFound from 'components/ui/NoDataFound';
import Loader from 'components/Loader';
import SupportDetails from 'components/support/support-details/SupportDetails';
import StyledHtmlText from 'components/ui/StyledHtmlText';
import PaginationComponent from 'components/ui/Pagination';
import Tabs from 'components/ui/Tabs';
import TimeRangeFilters from 'components/ui/TimeRangeFilters';
import { getDashboardSupportRequests } from 'helpers/http/dashboard';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import { SupportItemType } from '../dashboard/types/support.types';

const SupportRequestsWrapper = styled.div`
  .support-requests-header {
    background: ${(props) => props.theme.colors.white};
    border-radius: 12px;
    box-shadow: 0px 4px 35px rgba(164, 122, 60, 0.07);
    margin-bottom: 2rem;
  }

  .support-requests-content {
    background: ${(props) => props.theme.colors.white};
    border-radius: 12px;
    box-shadow: 0px 4px 35px rgba(164, 122, 60, 0.07);
  }
`;

export const SupportItem = styled.div`
  border: 1px solid ${(props) => props.theme.colors.gray6};
  border-radius: 10px;
  .request-details {
    max-width: 80%;
  }
  .light-text {
    opacity: 0.7;
  }
  .support-request--avatar {
    height: 38px;
    width: 38px;
    border-radius: 50%;
    object-fit: cover;
  }
  ${() => transition()};
`;

const FILTER_OPTIONS = {
  alltime: 'All time',
  today: 'Today',
  thisweek: 'This week',
  thismonth: 'This month',
  thisyear: 'This year',
  last3years: 'last 3 years',
};

const STATUS_FILTER_OPTIONS = {
  '': 'All',
  active: 'Open',
  closed: 'Closed',
};

const TAB_OPTIONS = [
  { id: 0, key: '', label: 'All' },
  { id: 1, key: 'general', label: 'General' },
  { id: 2, key: 'dispute', label: 'Dispute' },
  { id: 3, key: 'closures', label: 'Closures' },
];

const SupportRequests = () => {
  const RECORDS_PER_PAGE = 10;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeFilter, setActiveFilter] = useState('alltime');
  const [statusFilter, setStatusFilter] = useState('active');

  const [detailsModalState, setDetailsModalState] = useState<{
    show: boolean;
    supportItem?: SupportItemType;
  }>({
    show: false,
  });

  const [resolveModalState, setResolveModalState] = useState<{
    show: boolean;
    supportItem?: SupportItemType;
  }>({
    show: false,
  });

  const [isAccept, setAccept] = useState(false);

  /* Fetching support requests data */
  const { data, isLoading, isRefetching, refetch } = useQuery(
    [
      'get-support-requests',
      activeTab,
      currentPage,
      statusFilter,
      activeFilter,
    ],
    () =>
      getDashboardSupportRequests({
        activeTab,
        page: currentPage,
        limit: RECORDS_PER_PAGE,
        statusFilter,
        activeFilter,
      }),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  const onChangeFilter = (key: string) => {
    setActiveFilter(key);
    setCurrentPage(1);
  };

  const onChangeStatusFilter = (key: string) => {
    setStatusFilter(key);
    setCurrentPage(1);
  };

  const onTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const onPageChange = (page: { selected: number }) => {
    setCurrentPage(page?.selected + 1);
  };

  const onCloseDetailsModal = () => {
    setDetailsModalState({ show: false });
  };

  const showSupportDetails = (item) => () => {
    setDetailsModalState({ show: true, supportItem: item });
  };

  const openResolveModal = () => {
    setResolveModalState({
      show: true,
      supportItem: detailsModalState?.supportItem,
    });
    onCloseDetailsModal();
  };

  const openResolveAcceptModal = () => {
    setResolveModalState({
      show: true,
      supportItem: detailsModalState?.supportItem,
    });
    setAccept(true);
    onCloseDetailsModal();
  };

  const onCloseResolveModal = () => {
    setResolveModalState({
      show: false,
    });
  };

  const onResolving = () => {
    // Invalidate and refetch all support requests queries
    queryClient.invalidateQueries(['get-support-requests']);
    queryClient.invalidateQueries(['get-support-requests-closures']);
    queryClient.invalidateQueries(['get-support-requests-disputes']);
    refetch();
    onCloseResolveModal();
  };

  return (
    <SupportRequestsWrapper className="mt-3">
      {/* Header */}
      <div className="support-requests-header p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h1 className="fs-32 fw-700">Support Requests</h1>
          {/*<TimeRangeFilters
            options={FILTER_OPTIONS}
            activeFilter={activeFilter}
            onChange={onChangeFilter}
          />*/}
        </div>

        {/* Filters in one line */}
        <div className="d-flex align-items-center gap-4">
          {/* Tabs */}
          <div className="flex-grow-1">
            <Tabs
              tabs={TAB_OPTIONS}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <Tabs
              tabs={Object.entries(STATUS_FILTER_OPTIONS).map(
                ([key, label], index) => ({
                  label,
                  id: index,
                  key,
                })
              )}
              activeTab={statusFilter}
              onTabChange={onChangeStatusFilter}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="support-requests-content p-4">
        {isLoading && <Loader />}

        {!isLoading && !isRefetching && data?.data?.length === 0 && (
          <NoDataFound />
        )}

        {data?.data?.length > 0 &&
          data?.data?.map((item: SupportItemType, index: number) => (
            <SupportItem
              key={
                item?.dispute_id
                  ? `dispute_${item?.dispute_id}`
                  : item?.userdata?.user_id
                  ? `user_${item.userdata.user_id}_${item?.date_created}_${index}`
                  : `general_${item?.inquiry_id}_${index}`
              }
              className="mt-3 p-3 d-flex justify-content-between pointer flex-wrap gap-2"
              onClick={showSupportDetails(item)}
            >
              {/* Support request details */}
              <div className="request-details">
                <div className="support-title d-flex align-items-center flex-wrap">
                  <div className="fs-18 fw-400">
                    {item?.subject || item?.jobdata?.job_title}
                  </div>
                  {item?.dispute_id && (
                    <>
                      <div className="fs-sm fw-400 light-text">|</div>
                      <div className="fs-sm fw-400 light-text">
                        {item?.milestone?.title}
                      </div>
                    </>
                  )}
                </div>

                {/* Description */}
                <div className="fs-sm fw-400 mt-2 light-text">
                  <StyledHtmlText
                    htmlString={item?.description}
                    needToBeShorten={true}
                    fontSize={0.875}
                    id={
                      item?.dispute_id
                        ? `support-dispute_${item?.dispute_id}`
                        : item?.userdata?.user_id
                        ? `support-user_${item.userdata.user_id}_${item?.date_created}_${index}`
                        : `support-inquiry_${item?.inquiry_id}_${index}`
                    }
                  />
                </div>

                <div className="support-request--by mt-2 d-flex align-items-center gap-2">
                  <img
                    className="support-request--avatar"
                    src={item?.userdata?.user_image}
                  />
                  <div className="fs-1rem fw-400 light-text">
                    {item?.userdata?.first_name} {item?.userdata?.last_name}
                  </div>
                </div>
              </div>

              {/* Support request type badge and created date */}
              <div className="d-flex flex-column justify-content-between align-items-md-end gap-2">
                <div className="d-flex flex-row align-items-end gap-2">
                  {(item?.dispute_status == 'closed' ||
                    item?.status == 'closed') && (
                    <StatusBadge color="green" className="mt-2">
                      Closed
                    </StatusBadge>
                  )}
                  {(item?.dispute_id || item?.inquiry_id) && (
                    <StatusBadge color={item?.inquiry_id ? 'blue' : 'darkPink'}>
                      {item?.inquiry_id ? 'General' : 'Dispute'}
                    </StatusBadge>
                  )}
                  {!item?.inquiry_id && !item?.dispute_id && (
                    <StatusBadge color={'red'}>Closure</StatusBadge>
                  )}
                </div>
                <div className="fs-sm fw-400 light-text">
                  {item?.date_created
                    ? moment(item?.date_created).format('MMM DD, YYYY')
                    : '-'}
                </div>
              </div>
            </SupportItem>
          ))}

        {data?.data?.length > 0 && (
          <div className="d-flex justify-content-center mt-4 align-items-center">
            <PaginationComponent
              total={Math.ceil((data?.total || 0) / RECORDS_PER_PAGE)}
              onPageChange={onPageChange}
              currentPage={currentPage}
            />
          </div>
        )}
      </div>

      {/* Support details modal */}
      <SupportDetails
        show={detailsModalState?.show}
        onCloseModal={onCloseDetailsModal}
        onResolve={openResolveModal}
        selectedSupportItem={detailsModalState?.supportItem}
        refetch={refetch}
        openResolveAcceptModal={openResolveAcceptModal}
      />

      {/* Dispute resolve modal */}
      <DisputeResolveModal
        show={resolveModalState?.show}
        onCloseModal={onCloseResolveModal}
        supportItem={resolveModalState?.supportItem}
        onResolving={onResolving}
        isAccept={isAccept}
      />
    </SupportRequestsWrapper>
  );
};

export default SupportRequests;
